/**
 * API functions for the Shipment Tab Creation feature.
 *
 * All requests go through the project interceptor which handles
 * auth headers and error handling centrally.
 *
 * Endpoints:
 *   Saved Filter Sets — /shipment/saved-filter-sets/
 *   User Shipment Tabs — /shipment/tabs/
 */

import interceptor from '../common/interceptor';
import type {
  AppliedFilter,
  SavedFilterSet,
  ShipmentTab,
  TabsResponse,
  AvailableFilterSetsResponse,
  CreateSavedFilterSetBody,
  UpdateTabBody,
} from '@/pages/Shipments/ControlTower/types';

// ------------------------------------------------------------------
// Response envelope helpers
// ------------------------------------------------------------------

interface ApiEnvelope<T> {
  data: {
    data: T;
    is_success: boolean;
    message: string;
  };
}

function unwrap<T>(res: { data: ApiEnvelope<T> }): T {
  return res.data.data.data;
}

const BASE = '/shipment';

type RawSavedFilterSet = {
  id: string;
  name: string;
  visibility: SavedFilterSet['visibility'];
  leg_type?: SavedFilterSet['legType'];
  legType?: SavedFilterSet['legType'];
  filters?:
    | SavedFilterSet['filters']
    | {
        filterArray?: SavedFilterSet['filters'];
        appliedFilters?: AppliedFilter[];
      };
  presets?: AppliedFilter[];
  appliedFilters?: AppliedFilter[];
  team_ids?: string[];
  teamIds?: string[];
  teams_display?: SavedFilterSet['teamsDisplay'];
  teamsDisplay?: SavedFilterSet['teamsDisplay'];
  is_owner?: boolean;
  isOwner?: boolean;
  filter_count?: number;
  filterCount?: number;
  created_at?: string;
  createdAt?: string;
  updated_at?: string;
  updatedAt?: string;
};

type RawShipmentTab = {
  id: string;
  order: number;
  saved_filter_set?: RawSavedFilterSet;
  savedFilterSet?: RawSavedFilterSet;
  additional_filters?: AppliedFilter[];
  additionalFilters?: AppliedFilter[];
  sort_key?: string;
  sort_order?: ShipmentTab['sort_order'];
  created_by?: string;
  createdBy?: string;
};

const normalizeSavedFilterSet = (
  raw: RawSavedFilterSet | SavedFilterSet
): SavedFilterSet => {
  const rawFilters = raw.filters;

  const normalizedFilters = Array.isArray(rawFilters)
    ? rawFilters
    : (rawFilters?.filterArray ?? []);

  const appliedFilters =
    raw.appliedFilters ??
    (rawFilters && !Array.isArray(rawFilters)
      ? rawFilters.appliedFilters
      : undefined) ??
    ('presets' in raw ? raw.presets : undefined) ??
    [];

  const teamIds =
    raw.teamIds ?? ('team_ids' in raw ? raw.team_ids : undefined) ?? [];

  const teamsDisplay =
    raw.teamsDisplay ??
    ('teams_display' in raw ? raw.teams_display : undefined) ??
    [];

  const isOwner =
    raw.isOwner ?? ('is_owner' in raw ? raw.is_owner : undefined) ?? false;

  const filterCount =
    raw.filterCount ??
    ('filter_count' in raw ? raw.filter_count : undefined) ??
    Math.max(normalizedFilters.length, appliedFilters.length);

  const createdAt =
    raw.createdAt ?? ('created_at' in raw ? raw.created_at : undefined) ?? '';

  const updatedAt =
    raw.updatedAt ?? ('updated_at' in raw ? raw.updated_at : undefined) ?? '';

  return {
    id: raw.id,
    name: raw.name,
    visibility: raw.visibility,
    legType:
      raw.legType ?? ('leg_type' in raw ? raw.leg_type : undefined) ?? 'CL',
    filters: normalizedFilters,
    appliedFilters,
    teamIds,
    teamsDisplay,
    isOwner,
    filterCount,
    createdAt,
    updatedAt,
  };
};
const normalizeShipmentTab = (
  raw: RawShipmentTab | ShipmentTab
): ShipmentTab => {
  const savedFilterSet =
    raw.savedFilterSet ??
    ('saved_filter_set' in raw ? raw.saved_filter_set : undefined);
  const additionalFilters =
    raw.additionalFilters ??
    ('additional_filters' in raw ? raw.additional_filters : undefined) ??
    [];
  const createdBy =
    raw.createdBy ?? ('created_by' in raw ? raw.created_by : undefined) ?? '';
  return {
    id: raw.id,
    order: raw.order,
    savedFilterSet: normalizeSavedFilterSet(
      savedFilterSet ?? {
        id: '',
        name: '',
        visibility: 'personal',
      }
    ),
    additionalFilters,
    sort_key: 'sort_key' in raw ? raw.sort_key : undefined,
    sort_order: 'sort_order' in raw ? raw.sort_order : undefined,
    createdBy,
  };
};

const normalizeDefaultTab = (
  raw: TabsResponse['defaultTabs'][number] & {
    sort_key?: string;
    sort_order?: ShipmentTab['sort_order'];
  }
): TabsResponse['defaultTabs'][number] => ({
  ...raw,
});

const normalizeAvailableFilterSets = (
  raw:
    | {
        shared?: RawSavedFilterSet[];
        personal?: RawSavedFilterSet[];
      }
    | AvailableFilterSetsResponse
): AvailableFilterSetsResponse => ({
  shared: (raw.shared ?? []).map(normalizeSavedFilterSet),
  personal: (raw.personal ?? []).map(normalizeSavedFilterSet),
});

// ------------------------------------------------------------------
// Saved Filter Sets
// ------------------------------------------------------------------

export const getSavedFilterSets = async (
  token: string
): Promise<AvailableFilterSetsResponse> => {
  const res = await interceptor.axiosGet(
    'Get',
    `${BASE}/saved-filter-sets/`,
    token
  );
  return normalizeAvailableFilterSets(
    unwrap<AvailableFilterSetsResponse>(
      res as { data: ApiEnvelope<AvailableFilterSetsResponse> }
    )
  );
};

type CreateSavedFilterSetResponse = {
  saved_filter_set: RawSavedFilterSet;
  tab: ShipmentTab;
};

export const createSavedFilterSet = async (
  token: string,
  body: CreateSavedFilterSetBody
): Promise<{
  savedFilterSet: SavedFilterSet;
  tab?: ShipmentTab | null;
}> => {
  const payload = {
    name: body.name,
    visibility: body.visibility,
    leg_type: body.legType,
    filters: body.filters,
    presets: body.appliedFilters ?? [],
    team_ids: body.teamIds ?? [],
    filter_type: 'shipment',
    pin_to_tab: body.pin_to_tab ?? false,
  };

  const res = await interceptor.axiosPost(
    'Post',
    `${BASE}/saved-filter-sets/`,
    payload,
    token
  );

  const data = unwrap<CreateSavedFilterSetResponse>(
    res as { data: ApiEnvelope<CreateSavedFilterSetResponse> }
  );

  return {
    savedFilterSet: normalizeSavedFilterSet(data.saved_filter_set),
    tab: data.tab ? normalizeShipmentTab(data.tab) : null,
  };
};

export const updateSavedFilterSet = async (
  token: string,
  id: string,
  body: Partial<CreateSavedFilterSetBody>
): Promise<SavedFilterSet> => {
  const payload: Record<string, unknown> = {};
  if (body.name !== undefined) payload.name = body.name;
  if (body.visibility !== undefined) payload.visibility = body.visibility;
  if (body.legType !== undefined) payload.leg_type = body.legType;
  if (body.filters !== undefined) payload.filters = body.filters;
  if (body.appliedFilters !== undefined) payload.presets = body.appliedFilters;
  if (body.teamIds !== undefined) payload.team_ids = body.teamIds;

  const res = await interceptor.axiosPut(
    'Put',
    `${BASE}/saved-filter-sets/${id}/`,
    payload,
    token
  );
  return normalizeSavedFilterSet(
    unwrap<SavedFilterSet>(res as { data: ApiEnvelope<SavedFilterSet> })
  );
};

export const deleteSavedFilterSet = async (
  token: string,
  id: string
): Promise<void> => {
  await interceptor.axiosDelete(
    'Delete',
    `${BASE}/saved-filter-sets/${id}/`,
    undefined,
    token
  );
};

export const duplicateSavedFilterSet = async (
  token: string,
  id: string
): Promise<{ savedFilterSet: SavedFilterSet; tab: ShipmentTab }> => {
  const res = await interceptor.axiosPost(
    'Post',
    `${BASE}/saved-filter-sets/${id}/duplicate/`,
    {},
    token
  );
  const data = unwrap<{ saved_filter_set: SavedFilterSet; tab: ShipmentTab }>(
    res as {
      data: ApiEnvelope<{
        saved_filter_set: SavedFilterSet;
        tab: ShipmentTab;
      }>;
    }
  );
  return {
    savedFilterSet: normalizeSavedFilterSet(data.saved_filter_set),
    tab: normalizeShipmentTab(data.tab),
  };
};

// ------------------------------------------------------------------
// User Shipment Tabs
// ------------------------------------------------------------------

// DashboardTitle renders `tabsSlot` (a <TabStrip/> element) in two DOM
// locations at once -- one shown at desktop widths, one at mobile widths via
// CSS (`sm-d-none`/`sm-d-block`), both always mounted. That means two
// independent TabStrip instances both call this on mount, in the same tick.
// Rather than changing that layout (each placement is intentional --
// removing either would break one breakpoint's tab bar), collapse concurrent
// calls for the same token into a single in-flight request.
let inFlightTabsRequest: {
  token: string;
  promise: Promise<TabsResponse>;
} | null = null;

export const getShipmentTabs = async (token: string): Promise<TabsResponse> => {
  if (inFlightTabsRequest && inFlightTabsRequest.token === token) {
    return inFlightTabsRequest.promise;
  }

  const promise = (async (): Promise<TabsResponse> => {
    const res = await interceptor.axiosGet('Get', `${BASE}/tabs/`, token);

    const raw = unwrap<{
      default_tabs: TabsResponse['defaultTabs'];
      custom_tabs: TabsResponse['customTabs'];
    }>(
      res as {
        data: ApiEnvelope<{
          default_tabs: TabsResponse['defaultTabs'];
          custom_tabs: TabsResponse['customTabs'];
        }>;
      }
    );

    return {
      defaultTabs: raw.default_tabs.map(normalizeDefaultTab),
      customTabs: raw.custom_tabs.map(normalizeShipmentTab),
    };
  })();

  inFlightTabsRequest = { token, promise };
  try {
    return await promise;
  } finally {
    if (inFlightTabsRequest?.promise === promise) {
      inFlightTabsRequest = null;
    }
  }
};

export const pinFilterSetAsTab = async (
  token: string,
  savedFilterSetId: string
): Promise<ShipmentTab> => {
  const res = await interceptor.axiosPost(
    'Post',
    `${BASE}/tabs/`,
    { saved_filter_set_id: savedFilterSetId },
    token
  );
  return normalizeShipmentTab(
    unwrap<ShipmentTab>(res as { data: ApiEnvelope<ShipmentTab> })
  );
};

export const updateShipmentTab = async (
  token: string,
  tabId: string,
  body: UpdateTabBody
): Promise<ShipmentTab> => {
  const payload: Record<string, unknown> = {};
  if (body.order !== undefined) payload.order = body.order;
  if (body.additionalFilters !== undefined)
    payload.additional_filters = body.additionalFilters;

  // Use axiosPost with PATCH method string since the interceptor doesn't expose axiosPatch
  const res = await interceptor.axiosPost(
    'Patch',
    `${BASE}/tabs/${tabId}/`,
    payload,
    token
  );
  return normalizeShipmentTab(
    unwrap<ShipmentTab>(res as { data: ApiEnvelope<ShipmentTab> })
  );
};

export const hideShipmentTab = async (
  token: string,
  tabId: string
): Promise<void> => {
  await interceptor.axiosDelete(
    'Delete',
    `${BASE}/tabs/${tabId}/`,
    undefined,
    token
  );
};

export const getAvailableFilterSets = async (
  token: string
): Promise<AvailableFilterSetsResponse> => {
  const res = await interceptor.axiosGet(
    'Get',
    `${BASE}/tabs/available/`,
    token
  );
  return normalizeAvailableFilterSets(
    unwrap<AvailableFilterSetsResponse>(
      res as { data: ApiEnvelope<AvailableFilterSetsResponse> }
    )
  );
};

export const resetTabAdditionalFilters = async (
  token: string,
  tabId: string
): Promise<ShipmentTab> => {
  const res = await interceptor.axiosPost(
    'Post',
    `${BASE}/tabs/${tabId}/reset-additional-filters/`,
    {},
    token
  );
  return normalizeShipmentTab(
    unwrap<ShipmentTab>(res as { data: ApiEnvelope<ShipmentTab> })
  );
};
